import { useEffect, useState } from 'react'

export type CfoRuntimeMode = 'desktop-rich' | 'web-rich' | 'mobile-constrained'

export type CfoRuntimeProfile = {
  mode: CfoRuntimeMode
  reasons: string[]
}

export type CfoRuntimeProbe = {
  viewportWidth?: number | null
  coarsePointer?: boolean | null
  hoverNone?: boolean | null
  crossOriginIsolated?: boolean | null
  sharedArrayBuffer?: boolean | null
  storageQuotaBytes?: number | null
  deviceMemoryGb?: number | null
  desktopApp?: boolean | null
  override?: string | null
}

const MOBILE_OVERRIDE_VALUES = new Set(['mobile', 'mobile-constrained', 'constrained'])
const DESKTOP_OVERRIDE_VALUES = new Set(['desktop', 'desktop-rich', 'rich'])
const LOW_STORAGE_QUOTA_BYTES = 512 * 1024 * 1024

export const DEFAULT_CFO_RUNTIME_PROFILE: CfoRuntimeProfile = {
  mode: 'web-rich',
  reasons: [],
}

const normalizeOverride = (value: string | null | undefined): string => String(value || '').trim().toLowerCase()

export function classifyCfoRuntimeProbe(probe: CfoRuntimeProbe = {}): CfoRuntimeProfile {
  const override = normalizeOverride(probe.override)
  if (MOBILE_OVERRIDE_VALUES.has(override)) {
    return { mode: 'mobile-constrained', reasons: ['override:mobile'] }
  }
  if (DESKTOP_OVERRIDE_VALUES.has(override)) {
    return { mode: 'desktop-rich', reasons: ['override:desktop'] }
  }

  if (probe.desktopApp) {
    return { mode: 'desktop-rich', reasons: ['desktop_app'] }
  }

  const reasons: string[] = []
  const width = Number(probe.viewportWidth)
  if (Number.isFinite(width) && width > 0 && width <= 820) reasons.push('narrow_viewport')
  if (probe.coarsePointer === true) reasons.push('coarse_pointer')
  if (probe.hoverNone === true) reasons.push('hover_none')
  if (probe.sharedArrayBuffer === false) reasons.push('shared_array_buffer_unavailable')
  if (probe.crossOriginIsolated === false) reasons.push('cross_origin_isolated_unavailable')
  const quota = Number(probe.storageQuotaBytes)
  if (Number.isFinite(quota) && quota > 0 && quota <= LOW_STORAGE_QUOTA_BYTES) reasons.push('low_storage_quota')
  const memory = Number(probe.deviceMemoryGb)
  if (Number.isFinite(memory) && memory > 0 && memory <= 4) reasons.push('low_device_memory')

  if (reasons.length > 0) {
    return { mode: 'mobile-constrained', reasons }
  }

  return DEFAULT_CFO_RUNTIME_PROFILE
}

const readRuntimeOverride = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const params = new URLSearchParams(window.location.search || '')
    const queryOverride = params.get('fg_cfo_runtime')
    if (queryOverride) {
      window.localStorage?.setItem('fg_cfo_runtime', queryOverride)
      return queryOverride
    }
  } catch {
    // Debug override lookup must never block rendering.
  }
  try {
    return window.localStorage?.getItem('fg_cfo_runtime') || null
  } catch {
    return null
  }
}

const readRuntimeProbe = (storageQuotaBytes?: number | null): CfoRuntimeProbe => {
  if (typeof window === 'undefined') return {}
  const nav = window.navigator as Navigator & { deviceMemory?: number }
  const userAgent = String(nav.userAgent || '')
  return {
    viewportWidth: window.innerWidth,
    coarsePointer: window.matchMedia?.('(pointer: coarse)').matches ?? false,
    hoverNone: window.matchMedia?.('(hover: none)').matches ?? false,
    crossOriginIsolated: window.crossOriginIsolated === true,
    sharedArrayBuffer: typeof window.SharedArrayBuffer !== 'undefined',
    storageQuotaBytes,
    deviceMemoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    desktopApp: /Electron|FuseGridDesktop/i.test(userAgent) || Boolean((window as any).__TAURI__),
    override: readRuntimeOverride(),
  }
}

export function useCfoRuntimeProfile(): CfoRuntimeProfile {
  const [storageQuotaBytes, setStorageQuotaBytes] = useState<number | null>(null)
  const [profile, setProfile] = useState<CfoRuntimeProfile>(() => classifyCfoRuntimeProbe(readRuntimeProbe()))

  useEffect(() => {
    let cancelled = false
    const update = (quota = storageQuotaBytes) => {
      setProfile(classifyCfoRuntimeProbe(readRuntimeProbe(quota)))
    }
    update()
    const onResize = () => update()
    window.addEventListener('resize', onResize)
    const coarse = window.matchMedia?.('(pointer: coarse)')
    const hoverNone = window.matchMedia?.('(hover: none)')
    coarse?.addEventListener?.('change', onResize)
    hoverNone?.addEventListener?.('change', onResize)
    if (window.navigator.storage?.estimate) {
      void window.navigator.storage.estimate().then((estimate) => {
        if (cancelled) return
        const quota = typeof estimate.quota === 'number' ? estimate.quota : null
        setStorageQuotaBytes(quota)
        update(quota)
      }).catch(() => undefined)
    }
    return () => {
      cancelled = true
      window.removeEventListener('resize', onResize)
      coarse?.removeEventListener?.('change', onResize)
      hoverNone?.removeEventListener?.('change', onResize)
    }
  }, [storageQuotaBytes])

  return profile
}

export const isCfoMobileConstrained = (profile: CfoRuntimeProfile | null | undefined): boolean =>
  profile?.mode === 'mobile-constrained'
